import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { Box, CircularProgress, Paper, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { ConfigManager } from '../../../../../../application/config/ConfigManager';
import { useI18n } from '../../../../../../application/language/useI18n';
import { ICommonProperties } from '../../../../../../common/properties/ICommonProperties';
import { ApplicationAcceptStatus } from '../../../../../../service/application/entity/IApplicationModel';
import { Colors } from '../../../../../design/color/Colors';
import { FormActions } from '../../../../../design/form/FormActions';
import { FormTextField } from '../../../../../design/form/FormTextField';
import { ApplicationDetailDomain } from '../store/ApplicationDetailDomain';
import { ApplicationDetailsMode } from '../store/ApplicationDetailsMode';
import { ApplicationHeaderTabs } from './ApplicationHeaderTabs';
import { ApplicationInfoActions } from './ApplicationInfoActions';

export interface IApplicationInfoProperties extends ICommonProperties {
  domain: ApplicationDetailDomain;
}

export const ApplicationInfoContent = observer(({ domain }: IApplicationInfoProperties) => {
  const classes = useStyles();
  const { translate } = useI18n();
  const isEdit =
    domain.ui.mode.value === ApplicationDetailsMode.edit &&
    (domain.ui.isCanUpdate.value || domain.ui.isCanCreate.value);
  const activeTabKey = domain.ui.tabsData.value.tabs[domain.ui.tabsData.value.state]?.key;
  const newMessagesCount = Number(domain.ui.applicationNewMessagesState.value?.applicationNewMessages || 0);
  const isHaveNewMessages = newMessagesCount > 0;

  const createProjectLink = (projectId: string): string => {
    return `/project/${projectId}/info`;
  };
  const onSave = () => {
    if (domain.ui.editApplication.entity.acceptStatus === ApplicationAcceptStatus.accepted) {
      domain.setApplicationAcceptStatus(ApplicationAcceptStatus.draft);
    }
    domain.saveApplication();
  };

  useEffect(() => {
    domain.ui.changeTab(isEdit ? 'form' : domain.ui.tabsData.value.defaultViewKey);
  }, [isEdit]);
  return (
    <Box className={classes.container}>
      <Paper className={classes.root} elevation={0}>
        {!domain.projectFormDomain && !domain.ui.application.entity.isUnitedWithProject ? (
          domain.ui.project.entity.id && domain.ui.project.entity.name ? (
            <Link className={classes.link} to={createProjectLink(domain.ui.project.entity.id)}>
              <ArrowBackRoundedIcon fontSize="small" />
              <span>{`${translate('phrases.backToProject')}: ${domain.ui.project.entity.name}`}</span>
            </Link>
          ) : (
            <Typography variant="h6">{domain.ui.project.entity.name}</Typography>
          )
        ) : null}
        {isEdit && !domain.projectFormDomain && (
          <FormTextField
            errorMessage={domain.getApplicationValidationErrorFor('name')?.message}
            value={domain.ui.editApplication.entity.name}
            onChange={domain.getUpdateFieldHandler('name')}
            label={ConfigManager.getConfig()?.localization?.toLowerCase() === 'en' ? 'Title' : 'Название'}
            helperText={
              ConfigManager.getConfig()?.localization?.toLowerCase() === 'en'
                ? 'This field must contain the full name of the System'
                : 'Данное поле должно содержать полное название Системы'
            }
            required={true}
          />
        )}
        {!isEdit && domain.ui.application.entity.name && (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="h5">
              {`${translate('phrases.system')}: ${domain.ui.application.entity.name}`}
              {domain.ui.getAcceptStatusName()}
            </Typography>
            {isHaveNewMessages && (
              <Typography variant="caption">({newMessagesCount})</Typography>
            )}
          </Box>
        )}
        <Box sx={{ height: 12 }} />
        <ApplicationHeaderTabs domain={domain} />
      </Paper>

      <Box className={classes.tabContent}>
        {domain.ui.isCompletePageStartLoading.value ? (
          domain.ui.tabsData.value.tabs.map((item, index) => {
            const PageComponent = item.Component;
            const isActive = domain.ui.tabsData.value.state === index;

            return (
              <div
                key={item.key}
                className={item.key === 'form' ? classes.pageContentForm : classes.pageContent}
                style={{ display: isActive ? undefined : 'none' }}
              >
                <PageComponent domain={domain} />
              </div>
            );
          })
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: '24px 15px',
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
      {!isEdit && (
        <div className={classes.actionsContainer}>
          <ApplicationInfoActions domain={domain} />
        </div>
      )}
      {isEdit && (
        <div className={classes.actionsContainer}>
          <FormActions
            onCancel={domain.cancelEditApplication}
            onSave={onSave}
            disabled={domain.ui.isDisabledSaveButton.value}
          />
        </div>
      )}
    </Box>
  );
});

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    minHeight: 0,
  },
  root: {
    marginTop: 0,
    paddingTop: theme.spacing(1),
    paddingLeft: theme.spacing(4),
    flexShrink: 0,
  },
  tabContent: {
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
    backgroundColor: Colors.bgElevated,
  },
  pageContent: {
    height: '100%',
    overflowY: 'auto',
    minHeight: 0,
  },
  pageContentForm: {
    height: '100%',
    overflow: 'hidden',
    minHeight: 0,
  },
  actionsContainer: {
    flexShrink: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  link: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));
